/**
 * bubba-cdn.ts — Custom @nuxt/image provider for the Bubba CDN (cdn.bubba.pet / Cloudflare R2).
 *
 * Images are served as pre-generated AVIF variants uploaded by the admin Media CMS.
 * The provider maps width/format modifiers to the correct variant key suffix.
 *
 * Variant naming convention (set by the admin optimize endpoint):
 *   {base}@thumbnail.avif  — 200px wide
 *   {base}@medium.avif     — 400px wide
 *   {base}@large.avif      — 800px wide
 *   {base}@original.avif   — full size (AVIF re-encoded, no resize)
 *   {original-key}         — raw original file (served as-is, fallback)
 *
 * Width → variant mapping:
 *   w <= 200   → @thumbnail.avif
 *   w <= 400   → @medium.avif
 *   w <= 800   → @large.avif
 *   w >  800   → @original.avif (or raw if no AVIF variants exist)
 *   no width   → @large.avif (sensible default)
 *
 * Format override:
 *   If format=avif is explicitly requested, always return an AVIF variant.
 *   If format=original or no format, return raw URL (no @variant suffix).
 *
 * Usage in any Nuxt app:
 *   <NuxtImg src="/images/hero.png" width="400" />
 *   <!-- → https://cdn.bubba.pet/images/hero@medium.avif -->
 *
 *   <NuxtImg src="/images/hero.png" width="800" />
 *   <!-- → https://cdn.bubba.pet/images/hero@large.avif -->
 *
 *   <NuxtImg src="/images/hero.png" />
 *   <!-- → https://cdn.bubba.pet/images/hero@large.avif (default) -->
 *
 *   <NuxtImg src="/images/hero.png" format="original" />
 *   <!-- → https://cdn.bubba.pet/images/hero.png (raw, no variant) -->
 */

import { joinURL } from 'ufo';
import { defineProvider } from '@nuxt/image/runtime';

type VariantName = 'thumbnail' | 'medium' | 'large' | 'original';

const VARIANT_WIDTHS: Record<Exclude<VariantName, 'original'>, number> = {
  thumbnail: 200,
  medium: 400,
  large: 800,
};

/**
 * Pick the most appropriate variant for the given target width.
 * Returns null to indicate the raw original should be used (no @variant suffix).
 */
function pickVariant(width: number | undefined, format: string | undefined): VariantName | null {
  // Explicit raw request
  if (format === 'original' || format === 'raw') return null;

  // No width → default to large AVIF variant
  if (!width) return 'large';

  if (width <= VARIANT_WIDTHS.thumbnail) return 'thumbnail';
  if (width <= VARIANT_WIDTHS.medium) return 'medium';
  if (width <= VARIANT_WIDTHS.large) return 'large';

  // Wider than large → use @original.avif (AVIF re-encoded full-size)
  return 'original';
}

/**
 * Append the @{variant}.avif suffix to the source path.
 * Strips the original file extension and adds the variant suffix.
 */
function applyVariantSuffix(src: string, variant: VariantName): string {
  const dotIdx = src.lastIndexOf('.');
  const base = dotIdx !== -1 ? src.slice(0, dotIdx) : src;
  return `${base}@${variant}.avif`;
}

export default defineProvider<{ baseURL?: string }>({
  getImage(src, { modifiers, baseURL }) {
    const base = baseURL ?? 'https://cdn.bubba.pet';

    // SVGs are served as-is — skip AVIF variant logic entirely
    if (src.toLowerCase().endsWith('.svg')) {
      return { url: joinURL(base, src) };
    }

    const width = typeof modifiers?.width === 'number' ? modifiers.width : undefined;
    const format = typeof modifiers?.format === 'string' ? modifiers.format : undefined;

    const variant = pickVariant(width, format);

    const resolvedSrc = variant ? applyVariantSuffix(src, variant) : src;

    return {
      url: joinURL(base, resolvedSrc),
    };
  },
});
